import './ClassListProducts.scss'

import { DeleteOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, Col, Input, notification, Row, Select, Table } from 'antd'
import DeleteModal from 'components/okulyonetim-admin/delete-modal/DeleteModal'
import { saveAs } from 'file-saver'
import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { trNormalize } from 'utils/helpers'
import * as XLSX from 'xlsx'

import AppContext from '../../../../context/AppContext'
import {
  useOkulSinifUrunleriQuery,
  useSinifUrunuSilMutation
} from '../../../../gql/GeneratedGraphqlTypes'
import Spinner from '../../../spinner/Spinner'
import { ReactComponent as AktarIcon } from '../okul-siparis-page/icons/aktar.svg'

const { Search } = Input

const ClassListProducts: React.FC = () => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [searchValue, setSearchValue] = useState<string>('')
  const [selectedKampus, setSelectedKampus] = useState<string | undefined>(undefined)
  const [selectedDersIds, setSelectedDersIds] = useState<number[]>([])
  const [selectedOgretmenIds, setSelectedOgretmenIds] = useState<number[]>([])
  const [selectedSinifIds, setSelectedSinifIds] = useState<number[]>([])
  const { okul } = useContext(AppContext)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const { data, loading, refetch, error } = useOkulSinifUrunleriQuery({
    variables: {
      sube_id: okul?.id,
      arama: searchValue || null,
      sinif_sube_id: selectedKampus ?? null,
      ders_ids: selectedDersIds.length ? selectedDersIds.join(',') : null,
      ogretmen_ids: selectedOgretmenIds.length ? selectedOgretmenIds.join(',') : null,
      sinif_ids: selectedSinifIds.length ? selectedSinifIds.join(',') : null,
    },
    fetchPolicy: 'network-only',
  })

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [sinifUrunuSil] = useSinifUrunuSilMutation()

  const showDeleteModal = (ids: string[]) => {
    setDeleteId(ids.join(','))
    setIsDeleteModalVisible(true)
  }

  const handleDelete = async (ids: string) => {
    setSubmitting(true)
    const idList: number[] = ids.split(',').map((id) => Number(id))
    try {
      const response = await sinifUrunuSil({ variables: { id: idList } })
      if (response.data?.sinifUrunuSil.success) {
        notification.success({ message: '', description: `${response.data?.sinifUrunuSil?.message}`, duration: 6 })
        setIsDeleteModalVisible(false)
        setSelectedRowKeys([])
        await refetch()
      } else {
        notification.error({ message: '', description: `${response.data?.sinifUrunuSil?.message}`, duration: 6 })
      }
    } catch {
      notification.error({ message: '', description: 'Listeyi gönderirken bir hata oluştu.', duration: 6 })
    } finally {
      setSubmitting(false)
    }
  }

  useEffect(() => {
    refetch()
  }, [searchValue, selectedKampus, selectedDersIds, selectedOgretmenIds, selectedSinifIds, refetch])

  if (loading) return <Spinner showSpinner={loading} color="red" />
  if (submitting) return <Spinner showSpinner={submitting} color="red" />
  if (error) { console.error(error); navigate('/'); return null }

  const filteredData = data?.okulSinifUrunleri?.sinif_urunleri?.filter(Boolean) ?? []
  const totalItems = filteredData.length
  const displayedItems = selectedRowKeys.length

  const kampusOptions = data?.okulSinifUrunleri?.kampusler?.map((k) => ({ label: k?.isim, value: k?.id })) ?? []
  const dersOptions = data?.okulSinifUrunleri?.dersler?.map((d) => ({ label: d?.isim, value: d?.id })) ?? []
  const ogretmenOptions = data?.okulSinifUrunleri?.ogretmenler?.map((o) => ({ label: o?.isim, value: o?.id })) ?? []
  const sinifOptions = data?.okulSinifUrunleri?.siniflar?.map((s) => ({ label: s?.isim, value: s?.id })) ?? []

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', sorter: (a: any, b: any) => a.id - b.id, defaultSortOrder: 'descend' as const },
    { title: 'Sınıf', dataIndex: 'sinif', key: 'sinif', sorter: (a: any, b: any) => a.sinif?.localeCompare(b.sinif) },
    { title: 'Ders', dataIndex: 'ders', key: 'ders', sorter: (a: any, b: any) => a.ders?.localeCompare(b.ders) },
    { title: 'Barkod', dataIndex: 'barkod', key: 'barkod', sorter: (a: any, b: any) => a.barkod?.localeCompare(b.barkod) },
    { title: 'Ürün', dataIndex: 'urun', key: 'urun', sorter: (a: any, b: any) => a.urun?.localeCompare(b.urun) },
    {
      title: 'Sınıf Not', dataIndex: 'sinif_not', key: 'sinif_not',
      render: (value: string | null | undefined) => (!value || value === 'undefined' || value === 'null') ? '' : value,
    },
    { title: 'Adet', dataIndex: 'adet', key: 'adet', sorter: (a: any, b: any) => a.adet - b.adet },
    { title: 'Mevcut', dataIndex: 'mevcut', key: 'mevcut' },
    { title: 'Oluşturma Tarihi', dataIndex: 'olusturma_tarihi', key: 'olusturma_tarihi' },
  ]

  const exportAllToExcel = () => {
    const headers = ['id', 'sinifdersi_id', 'sinif_id', 'sinif_adi', 'ders_id', 'ders_adi', 'urun_id', 'urun_stok_id', 'urun_adi', 'adet', 'barkod', 'olusturma_tarihi', 'ogretmen_id', 'ogretmen_adi', 'ogretmen_soyadi', 'ogretmen_mail', 'sinif_not']
    const rows = data?.okulSinifUrunleri?.sinif_urunleri.map((su: any) => [
      su.id || '',
      su.sinifdersi_id || '',
      su.sinif_id || '',
      su.sinif || '',
      su.ders_id || '',
      su.ders || '',
      su.urun_id || '',
      su.urun_stok_id || '',
      su.urun || '',
      su.adet || '',
      su.barkod || '',
      su.olusturma_tarihi || '',
      su.ogretmen_id || '',
      su.ogretmen_adi || '',
      su.ogretmen_soyadi || '',
      su.ogretmen_mail || '',
      su.sinif_not && su.sinif_not !== 'undefined' ? su.sinif_not : '',
    ]) ?? []

    const worksheetData = [headers, ...rows]
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData)
    const colWidths = headers.map((_: string, i: number) =>
      Math.max(...worksheetData.map((row) => (row[i] ? row[i].toString().length : 10)))
    )
    worksheet['!cols'] = colWidths.map((w: number) => ({ wch: w }))
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'TümVeriler')
    saveAs(new Blob([XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })]), 'sinifUrunler.xlsx')
  }

  return (
    <div className="okul-siniflisteurunleri-page">
      <div className="okul-sepeti-admin-header">
        <h1>Sınıf Listeleri</h1>
        <div className="export-buttons">
          <Button onClick={exportAllToExcel} className="export-button">
            <AktarIcon /> Dışa Aktar
          </Button>
          <Button onClick={() => navigate('/okulyonetimadmin/dersurunu/ice-aktar')} className="export-button">
            <AktarIcon /> İçe Aktar
          </Button>
        </div>
      </div>

      <Row gutter={[8, 8]} className="filter-section">
        <Col span={6}>
          <Search
            placeholder="Sınıf Listesi Ara"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onSearch={() => setSearchValue(searchTerm)}
            allowClear
            enterButton={<div className="search-suffix-div">Ara <SearchOutlined /></div>}
          />
        </Col>
        <Col span={4}>
          <Select
            placeholder="Kampüs"
            value={selectedKampus}
            onChange={(value) => setSelectedKampus(value)}
            allowClear
            style={{ width: '100%' }}
            options={kampusOptions}
          />
        </Col>
        <Col span={4}>
          <Select
            mode="multiple"
            placeholder="Ders"
            value={selectedDersIds}
            onChange={(value) => setSelectedDersIds(value)}
            allowClear
            showSearch
            filterOption={(input, option) =>
              trNormalize(option?.label as string).includes(trNormalize(input))
            }
            style={{ width: '100%' }}
            options={dersOptions}
            maxTagCount="responsive"
          />
        </Col>
        <Col span={5}>
          <Select
            mode="multiple"
            placeholder="Öğretmen (e-posta)"
            value={selectedOgretmenIds}
            onChange={(value) => setSelectedOgretmenIds(value)}
            allowClear
            showSearch
            filterOption={(input, option) =>
              trNormalize(option?.label as string).includes(trNormalize(input))
            }
            style={{ width: '100%' }}
            options={ogretmenOptions}
            maxTagCount="responsive"
          />
        </Col>
        <Col span={5}>
          <Select
            mode="multiple"
            placeholder="Sınıf"
            value={selectedSinifIds}
            onChange={(value) => setSelectedSinifIds(value)}
            allowClear
            showSearch
            filterOption={(input, option) =>
              trNormalize(option?.label as string).includes(trNormalize(input))
            }
            style={{ width: '100%' }}
            options={sinifOptions}
            maxTagCount="responsive"
          />
        </Col>
      </Row>

      <Table
        dataSource={filteredData as object[]}
        columns={columns}
        rowClassName={(_, index) => (index % 2 === 0 ? 'table-row-light' : 'table-row-dark')}
        pagination={{
          current: currentPage,
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          total: totalItems,
          showTotal: (total) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Button
                style={{ display: selectedRowKeys.length === 0 ? 'none' : '' }}
                icon={<DeleteOutlined />}
                onClick={() => showDeleteModal(selectedRowKeys.map((id) => id.toString()))}
                type="primary"
                danger
              >
                Seçilen {selectedRowKeys.length} Sınıf Listelerini Sil
              </Button>
              <span>Seçilen: {displayedItems} / Toplam: {total}</span>
            </div>
          ),
          onChange: (page, size) => { setCurrentPage(page); setPageSize(size) },
        }}
        loading={loading}
        rowKey="id"
        className="okul-yonetim-admin-table"
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys as string[]),
        }}
      />

      <DeleteModal
        title="Seçilen Sınıf Listeleri Sil"
        id={deleteId || ''}
        visible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onDelete={handleDelete}
      />
    </div>
  )
}

export default ClassListProducts
